import { useEffect, useState } from "react";

import {
  RadioButton,
  TextArea,
  Table,
  TableMetadata,
  Typography,
  useTable,
  Label,
} from "@king-ict/eupisi-ui/components";
import { hrDate } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";

import { DEFAULT_COUNTY_COMMITTEE } from "./constants";
import { getCommitteeEmployeeDisplayName } from "./utils";
import { getCountyCommitteeById } from "../../api/Committee/CountyCommitteeApi";
import { getSchoolYearCurrent } from "../../api/SchoolYear/SchoolYearApi";
import { ContentArea, FormCard, ToolbarTop } from "../../components";
import { useEnumCodeBooks } from "../../hooks";
import { useAuthCounties } from "../../hooks/auth";
import { CommitteeEmployee, CountyCommittee } from "../../models/Committee";
import { ImportantDateEventEnum } from "../../models/ImportantDate/ImportantDateEventEnum";
import {
  countyCommitteeEditUrl,
  countyCommitteeListRoute,
} from "../../routes/routes";
import { FormatUtils } from "../../utils";

export function CountyCommitteeDetails() {
  const { id } = useParams<{ id: string; countyId: string }>();
  const { t } = useTranslation();
  const history = useHistory();
  const counties = useAuthCounties();

  const [item, setItem] = useState<CountyCommittee>(DEFAULT_COUNTY_COMMITTEE);
  const [tableData, setTableData] = useState<CommitteeEmployee[]>([]);
  const [isEditable, setIsEditable] = useState(false);
  const tableMetadata: TableMetadata<CommitteeEmployee> = {
    rowKey: "id",
    columns: [
      {
        headerClassName: "col-3",
        columnKey: "expertCommissionRole",
        header: t("common.expertCommission.role"),
        accessor: (item: CommitteeEmployee) => {
          return <div>{item.committeeRole?.name}</div>;
        },
      },
      {
        headerClassName: "col-6",
        columnKey: "employee",
        header: t("common.expertCommission.employee"),
        accessor: (item: CommitteeEmployee) => {
          return <div>{getCommitteeEmployeeDisplayName(item)}</div>;
        },
      },
      {
        headerClassName: "col-4",
        columnKey: "activeTo",
        header: t("common.expertCommission.activeTo"),
        accessor: ({ deactivationDate }) => {
          return <div>{FormatUtils.formatDate(deactivationDate)}</div>;
        },
      },
      {
        headerClassName: "col-1",
        columnKey: "expertCommissionPosition",
        header: t("common.expertCommission.position"),
        accessor: (item: CommitteeEmployee) => {
          return <div>{item.committeePosition?.name}</div>;
        },
      },
      {
        headerClassName: "col-1",
        columnKey: "president",
        header: t("common.expertCommission.president"),
        accessor: (emp: CommitteeEmployee) => {
          return (
            <div>
              <RadioButton
                ariaLabel="item.president"
                key={emp.committeeRole?.id}
                name={"item.president"}
                checked={emp.isPresident}
                onChange={() => {}}
                value={emp.committeeRole!.id}
                disabled
              />
            </div>
          );
        },
      },
    ],
    rowToolTip: () => undefined,
  };

  const [tableProps] = useTable({
    metadata: tableMetadata,
    data: tableData,
    pagination: false,
    paginationPage: undefined,
    paginationPageSize: undefined,
    totalItems: 1,
    defaultSort: {},
  });
  const { committeeStatusCodeBook: statuses } = useEnumCodeBooks();

  useEffect(() => {
    (async () => {
      const schoolYear = await getSchoolYearCurrent();
      if (schoolYear) {
        const importantDate = schoolYear.importantDates.find(
          (val) =>
            val.eventId === ImportantDateEventEnum.InstitutionAdministration
        );
        if (importantDate) {
          const exactlyNow = new Date(Date.now());
          const dateFrom = new Date(importantDate.dateTimeFrom);
          const dateTo = new Date(importantDate.dateTimeTo);
          if (dateFrom <= exactlyNow && dateTo >= exactlyNow) {
            setIsEditable(true);
          }
        }
      }
      if (!isNaN(+id) && +id > 0) {
        const result = await getCountyCommitteeById(+id);
        if (result) {
          setItem(result);
          setTableData(result.employees!);
        }
      }
    })();
  }, [id]);

  const goBack = () => {
    history.push(countyCommitteeListRoute);
  };
  const handleEditClicked = () => {
    history.push(countyCommitteeEditUrl(+id));
  };

  return (
    <ContentArea>
      <ToolbarTop
        showAdd={"hide"}
        showBack={"show"}
        showEdit={isEditable ? "show" : "disable"}
        pageTitle={t("common.expertCommission.countyPageNameEdit")}
        onClickBack={goBack}
        onClickEdit={handleEditClicked}
      />

      <FormCard>
        <Row>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.expertCommission.county")}
            </Typography>
          </Col>
          <Col md={10}>
            <Typography variant="base" color="light">
              {counties.find((c) => c.id === item.countyId)?.name}
            </Typography>
          </Col>
        </Row>
        <Row>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.expertCommission.dateFromDateTo")}
            </Typography>
          </Col>
          <Col md={4}>
            <Typography variant="base" color="light">
              {item.dateFrom ? hrDate(new Date(item.dateFrom)) : ""} -{" "}
              {item.dateTo ? hrDate(new Date(item.dateTo)) : ""}
            </Typography>
          </Col>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.status")}
            </Typography>
          </Col>
          <Col md={4}>
            <Label
              color={FormatUtils.getCommitteeStatusColor(item.statusId)}
              className="text-center"
              outline={false}
              fullWidth={false}
            >
              {statuses.find((v) => v.id === item.statusId)?.name}
            </Label>
          </Col>
        </Row>
      </FormCard>

      <FormCard>
        <Table {...tableProps} />
      </FormCard>

      <FormCard>
        <Row>
          <Col md={12}>
            <TextArea
              ariaLabel="item.note"
              label={t("common.expertCommission.note")}
              name={"item.note"}
              value={item.note ?? ""}
              onChange={() => {}}
              maxLength={5000}
              disabled
            />
          </Col>
        </Row>
      </FormCard>
    </ContentArea>
  );
}
